// quickActionCalculations.ts - Utility functions for Quick Action calculations

import { PlanInfo, SpaceInfo, TenantInfo } from "../../../../../types/plan";
import { TableData } from "../../../types/table";

// Deal stages that qualify for Future Tenant calculation
const QUALIFYING_DEAL_STAGES = [
  "executed",
  "signed, fixed rent commence",
  "signed, variable rent commence",
  "open",
  "mutually approved",
];

/**
 * Parse a number from a string or return the number if already a number
 */
function parseNumber(value: string | number | undefined | null): number {
  if (value === undefined || value === null || value === "") return 0;
  if (typeof value === "number") return value;
  // Remove currency symbols, commas, and parse
  const cleaned = value.replace(/[$,]/g, "").trim();
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Parse a date string and return a Date object
 * Handles formats: MM/DD/YYYY, YYYY-MM-DD, ISO format
 */
function parseDate(dateStr: string | undefined | null): Date | null {
  if (!dateStr) return null;

  // Try MM/DD/YYYY format first (common in this data)
  const mmddyyyyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mmddyyyyMatch) {
    const [, month, day, year] = mmddyyyyMatch;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return isNaN(date.getTime()) ? null : date;
  }

  // Try standard Date parsing (ISO format, etc.)
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Calculate days between two dates
 */
function daysBetween(date1: Date, date2: Date): number {
  const diffTime = date2.getTime() - date1.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Get tenant info from a space's detailTabs
 * Matches partial caption: "Tenant Information" or "Future Tenant Information"
 */
function getTenantInfo(
  space: SpaceInfo,
  tabType: "current" | "future"
): TenantInfo | null {
  const searchPattern = tabType === "future"
    ? "future tenant information"
    : "tenant information";

  const tab = space.detailTabs?.find((t) => {
    const caption = t.caption.toLowerCase();
    if (tabType === "future") {
      return caption.includes("future tenant information");
    } else {
      // Match "Tenant Information" but NOT "Future Tenant Information"
      return caption.includes("tenant information") && !caption.includes("future");
    }
  });
  return tab?.detail || null;
}

/**
 * Check if a deal stage qualifies for Future Tenant calculation
 */
function isQualifyingDealStage(dealStage: string | undefined | null): boolean {
  if (!dealStage) return false;
  const normalized = dealStage.toLowerCase().trim();
  return QUALIFYING_DEAL_STAGES.some((stage) => normalized.includes(stage));
}

/**
 * Get spaces data from plansInfo based on selected space identifiers
 * Can match by spaceNumber OR spaceHeader (tenant name)
 */
export function getSelectedSpacesData(
  plansInfo: PlanInfo | undefined,
  selectedSpaceNumbers: string[]
): SpaceInfo[] {
  if (!plansInfo?.spaces || selectedSpaceNumbers.length === 0) return [];

  // Normalize selected IDs for comparison (replace _ with space, lowercase)
  const normalizedSelected = selectedSpaceNumbers.map((s) =>
    s.replaceAll("_", " ").toLowerCase().trim()
  );

  console.log("getSelectedSpacesData - Normalized selected IDs:", normalizedSelected);

  return plansInfo.spaces.filter((space) => {
    // Normalize spaceNumber
    const normalizedSpaceNum = (space.spaceNumber || "")
      .replaceAll("_", " ")
      .toLowerCase()
      .trim();

    // Normalize spaceHeader (tenant name)
    const normalizedSpaceHeader = (space.spaceHeader || "")
      .replaceAll("_", " ")
      .toLowerCase()
      .trim();

    // Check if any selected ID matches spaceNumber OR spaceHeader
    return normalizedSelected.some((selectedId) => {
      return (
        selectedId === normalizedSpaceNum ||
        selectedId === normalizedSpaceHeader ||
        // Also check with underscores for exact match
        selectedId === space.spaceNumber?.toLowerCase() ||
        selectedId === space.spaceHeader?.toLowerCase()
      );
    });
  });
}

/**
 * Calculate Financial Summary for selected spaces
 */
export function calculateFinancialSummary(
  spaces: SpaceInfo[]
): TableData | null {
  if (spaces.length === 0) return null;

  const data: Record<string, any>[] = [];
  let totalSalesR12 = 0;
  let totalRent = 0;
  let totalGLA = 0;
  let totalOccupancyCost = 0;
  let validOccupancyCount = 0;

  for (const space of spaces) {
    const currentTenant = getTenantInfo(space, "current");

    // Get values, preferring Current Tenant data
    const gla = parseNumber(currentTenant?.GLA || currentTenant?.["Actual Footprint SF"]);
    const salesR12 = parseNumber(currentTenant?.["Sales R12"]);
    const totalRentValue = parseNumber(
      currentTenant?.["Total Rent"] || currentTenant?.["Gross Rent"]
    );
    const totalRentPSF = parseNumber(
      currentTenant?.["Total Rent PSF"] || currentTenant?.["Gross Rent PSF"]
    );
    const occupancyCost = parseNumber(
      currentTenant?.["Occupancy Cost"] || currentTenant?.["Occupancy Cost %"]
    );

    // Accumulate totals
    totalSalesR12 += salesR12;
    totalRent += totalRentValue;
    totalGLA += gla;
    if (occupancyCost > 0) {
      totalOccupancyCost += occupancyCost;
      validOccupancyCount++;
    }

    data.push({
      space_number: space.spaceNumber || "N/A",
      tenant_name: currentTenant?.DBA || space.spaceHeader || "Vacant",
      gla_sf: gla,
      sales_r12_usd: salesR12,
      total_rent_usd: totalRentValue,
      rent_per_sf: totalRentPSF,
      occupancy_cost_percent: occupancyCost,
    });
  }

  // Add summary row
  const avgSalesR12 = spaces.length > 0 ? totalSalesR12 / spaces.length : 0;
  const avgRent = spaces.length > 0 ? totalRent / spaces.length : 0;
  const avgRentPSF = totalGLA > 0 ? totalRent / totalGLA : 0;
  const avgOccupancyCost =
    validOccupancyCount > 0 ? totalOccupancyCost / validOccupancyCount : 0;

  data.push({
    space_number: "TOTAL / AVERAGE",
    tenant_name: `${spaces.length} space(s)`,
    gla_sf: totalGLA,
    sales_r12_usd: totalSalesR12,
    total_rent_usd: totalRent,
    rent_per_sf: avgRentPSF,
    occupancy_cost_percent: avgOccupancyCost,
    _is_summary: true,
  });

  return {
    labels: {
      space_number: "Space #",
      tenant_name: "Tenant",
      gla_sf: "GLA (SF)",
      sales_r12_usd: "Sales R12",
      total_rent_usd: "Total Rent",
      rent_per_sf: "Rent/SF",
      occupancy_cost_percent: "Occupancy Cost %",
    },
    data,
  };
}

/**
 * Calculate Opportunity Cost for selected spaces
 */
export function calculateOpportunityCost(
  spaces: SpaceInfo[]
): TableData | null {
  if (spaces.length === 0) return null;

  const today = new Date();
  const data: Record<string, any>[] = [];
  let totalOpportunityCost = 0;

  for (const space of spaces) {
    const currentTenant = getTenantInfo(space, "current");
    const futureTenant = getTenantInfo(space, "future");

    // Current Tenant calculation
    const currentTotalRent = parseNumber(
      currentTenant?.["Total Rent"] || currentTenant?.["Gross Rent"]
    );
    const leaseEndDate = parseDate(currentTenant?.["Lease End Date"]);

    let currentTenantCharge = 0;
    let daysUntilExpiration = 0;

    if (leaseEndDate && leaseEndDate > today) {
      daysUntilExpiration = daysBetween(today, leaseEndDate);
      currentTenantCharge = (currentTotalRent * daysUntilExpiration) / 365;
    }

    // Future Tenant calculation (only if qualifying deal stage)
    // Future Tenant Charge = Total Rent × (Rent Expiration Date - Lease Start Date) / 365
    let futureTenantCharge = 0;
    let futureTenantName = "";
    let futureTotalRent = 0;
    let futureRentExpiration: Date | null = null;
    let futureLeaseStartDate: Date | null = null;
    let futureLeaseDays = 0;

    if (futureTenant && isQualifyingDealStage(futureTenant["Deal Stage"])) {
      futureTenantName = futureTenant.DBA || futureTenant["Deal Name"] || "Future Tenant";
      futureTotalRent = parseNumber(
        futureTenant["Total Rent"] || futureTenant["Gross Rent"]
      );
      futureRentExpiration = parseDate(futureTenant["Rent Expiration Date"]);
      // Try multiple possible field names for the start date
      futureLeaseStartDate = parseDate(
        futureTenant["Lease Start Date"] ||
        futureTenant["Projected Rent Commencement Date"] ||
        futureTenant["Date of Possession (Projected Per TC)"] ||
        futureTenant["Date of Possession (Mutually Agreed)"]
      );

      if (futureRentExpiration && futureLeaseStartDate && futureRentExpiration > futureLeaseStartDate) {
        futureLeaseDays = daysBetween(futureLeaseStartDate, futureRentExpiration);
        futureTenantCharge = (futureTotalRent * futureLeaseDays) / 365;
      }
    }

    const spaceOpportunityCost = currentTenantCharge + futureTenantCharge;
    totalOpportunityCost += spaceOpportunityCost;

    data.push({
      space_number: space.spaceNumber || "N/A",
      current_tenant: currentTenant?.DBA || space.spaceHeader || "Vacant",
      current_total_rent_usd: currentTotalRent,
      lease_end_date: leaseEndDate
        ? leaseEndDate.toISOString().split("T")[0]
        : "N/A",
      days_until_expiration: daysUntilExpiration,
      current_tenant_charge_usd: currentTenantCharge,
      future_tenant: futureTenantName || "N/A",
      future_total_rent_usd: futureTotalRent || 0,
      future_lease_start: futureLeaseStartDate
        ? futureLeaseStartDate.toISOString().split("T")[0]
        : "N/A",
      future_rent_expiration: futureRentExpiration
        ? futureRentExpiration.toISOString().split("T")[0]
        : "N/A",
      future_lease_days: futureLeaseDays,
      future_tenant_charge_usd: futureTenantCharge,
      opportunity_cost_usd: spaceOpportunityCost,
    });
  }

  // Add total row
  data.push({
    space_number: "TOTAL",
    current_tenant: `${spaces.length} space(s)`,
    current_total_rent_usd: data.reduce(
      (sum, row) => sum + (row.current_total_rent_usd || 0),
      0
    ),
    lease_end_date: "-",
    days_until_expiration: "-",
    current_tenant_charge_usd: data.reduce(
      (sum, row) => sum + (row.current_tenant_charge_usd || 0),
      0
    ),
    future_tenant: "-",
    future_total_rent_usd: data.reduce(
      (sum, row) => sum + (row.future_total_rent_usd || 0),
      0
    ),
    future_lease_start: "-",
    future_rent_expiration: "-",
    future_lease_days: "-",
    future_tenant_charge_usd: data.reduce(
      (sum, row) => sum + (row.future_tenant_charge_usd || 0),
      0
    ),
    opportunity_cost_usd: totalOpportunityCost,
    _is_summary: true,
  });

  return {
    labels: {
      space_number: "Space #",
      current_tenant: "Current Tenant",
      current_total_rent_usd: "Current Rent",
      lease_end_date: "Lease End",
      days_until_expiration: "Days Left",
      current_tenant_charge_usd: "Current Charge",
      future_tenant: "Future Tenant",
      future_total_rent_usd: "Future Rent",
      future_lease_start: "Future Start",
      future_rent_expiration: "Future Exp.",
      future_lease_days: "Future Days",
      future_tenant_charge_usd: "Future Charge",
      opportunity_cost_usd: "Opportunity Cost",
    },
    data,
  };
}

/**
 * Round to 2 decimals for clean deal-sheet figures.
 */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Calculate the Wing Analysis for selected spaces: a comparison of similar-sized
 * tenants' sales and occupancy costs in a mall wing. Produces per-space detail
 * rows plus a GLA-weighted "WING AVERAGE" row (Sales PSF, Occ Cost PSF, Occ Cost %),
 * matching the deal-sheet "Wing Avg" columns.
 */
export function calculateWingAnalysis(spaces: SpaceInfo[]): TableData | null {
  if (spaces.length === 0) return null;

  const data: Record<string, any>[] = [];
  let totalGLA = 0;
  let totalSales = 0;
  let totalOccCost = 0;

  for (const space of spaces) {
    const currentTenant = getTenantInfo(space, "current");

    const gla = parseNumber(
      currentTenant?.GLA || currentTenant?.["Actual Footprint SF"]
    );

    const salesR12 = parseNumber(currentTenant?.["Sales R12"]);
    const occCost = parseNumber(currentTenant?.["Occupancy Cost PSF"]) * gla; // Convert % to absolute cost for calculations

    // Prefer explicit PSF/% fields; fall back to derived values when missing.
    const salesPSF =
      parseNumber(currentTenant?.["Sales R12 PSF"]) ||
      (gla > 0 ? salesR12 / gla : 0);
    const occCostPSF =
      parseNumber(currentTenant?.["Occupancy Cost PSF"]) ||
      (gla > 0 ? occCost / gla : 0);
    const occCostPct =
      (parseNumber(currentTenant?.["Occupancy Cost %"]) || parseNumber(currentTenant?.["Occupancy Cost"])) ||
      (salesR12 > 0 ? (occCost / salesR12) * 100 : 0);

    totalGLA += gla;
    totalSales += salesR12;

    totalOccCost += occCost;

    data.push({
      space_number: space.spaceNumber || "N/A",
      tenant_name: currentTenant?.DBA || space.spaceHeader || "Vacant",
      gla_sf: gla,
      sales_psf: round2(salesPSF),
      occ_cost_psf: round2(occCostPSF),
      occ_cost_percent: round2(occCostPct),
    });
  }

  console.log({
    totalGLA,
    totalSales,
    totalOccCost,
  })

  // GLA-weighted wing averages (standard for PSF / % roll-ups).
  const avgSalesPSF = totalGLA > 0 ? totalSales / totalGLA : 0;
  const avgOccCostPSF = totalGLA > 0 ? totalOccCost / totalGLA : 0;
  const avgOccCostPct = totalSales > 0 ? (totalOccCost / totalSales) * 100 : 0;

  data.push({
    space_number: "WING AVERAGE",
    tenant_name: `${spaces.length} space(s) : ${data.map((row) => row.space_number).join(", ")}`,
    // Average GLA per space (the summary row is an average, not a total).
    gla_sf: round2(totalGLA / spaces.length),
    sales_psf: round2(avgSalesPSF),
    occ_cost_psf: round2(avgOccCostPSF),
    occ_cost_percent: round2(avgOccCostPct),
    _is_summary: true,
  });

  return {
    labels: {
      space_number: "Space #",
      tenant_name: "Tenant",
      gla_sf: "GLA (SF)",
      sales_psf: "Sales PSF",
      occ_cost_psf: "Occ Cost PSF",
      occ_cost_percent: "Occ Cost %",
    },
    data,
  };
}

/**
 * Generate a message content for the quick action result
 */
export function generateQuickActionMessage(
  actionType: "Financial Summary" | "Opportunity Cost" | "Wing Analysis",
  spacesCount: number,
  propertyName?: string
): string {
  const property = propertyName ? ` for ${propertyName}` : "";

  if (actionType === "Financial Summary") {
    return `Here's the Financial Summary for ${spacesCount} selected space${spacesCount > 1 ? "s" : ""}${property}. The table shows key financial metrics including Sales R12, Total Rent, Rent per SF, and Occupancy Cost.`;
  } else if (actionType === "Wing Analysis") {
    return `Here's the Wing Analysis for ${spacesCount} selected space${spacesCount > 1 ? "s" : ""}${property}. The table compares each tenant's Sales PSF and Occupancy Cost, with GLA-weighted wing averages. The wing spaces are highlighted on the plan and the highlighted plan can be exported.`;
  } else {
    return `Here's the Opportunity Cost analysis for ${spacesCount} selected space${spacesCount > 1 ? "s" : ""}${property}. This calculates the remaining lease value based on current and future tenant agreements.`;
  }
}

/**
 * Resolve the tenant name and merchandise category for a single selected space
 * (used by the AI-backed "Tenant Ranking" / "Tenant Comparison" quick actions).
 */
export function getTenantContextForSpace(space: SpaceInfo): {
  tenantName: string;
  merchandiseCategory: string;
} {
  const tenant = getTenantInfo(space, "current");
  return {
    tenantName: tenant?.DBA || space.spaceHeader || "",
    merchandiseCategory: (tenant?.["Merchandise Category"] as string) || "",
  };
}

/**
 * Build the prompt for the portfolio-wide "Tenant Ranking" quick action.
 */
export function buildTenantRankingPrompt(tenantName: string): string {
  return (
    `List every location of tenant "${tenantName}" across the entire portfolio, ` +
    `ranked by sales per square foot (highest first). For each location include: ` +
    `property name, space number, GLA (sq ft), sales R12, sales per sq ft, and ` +
    `occupancy cost %. Also state the total number of locations of "${tenantName}" ` +
    `across the portfolio. Return the result as a table.`
  );
}

/**
 * Build the prompt for the "Tenant Comparison" quick action (peers in the same
 * merchandise category at the given property).
 */
export function buildTenantComparisonPrompt(
  tenantName: string,
  merchandiseCategory: string,
  propertyName: string
): string {
  const category = merchandiseCategory
    ? ` in the same merchandise category ("${merchandiseCategory}")`
    : " in the same merchandise category";
  const atProperty = propertyName ? ` at property "${propertyName}"` : "";
  return (
    `Compare tenant "${tenantName}"${atProperty} against the other tenants${category}. ` +
    `Exclude from the comparison any tenant whose sales (sales per sq ft or sales R12) ` +
    `is zero, null, or missing — do not include them in the ranking or in the category average. ` +
    `Rank the remaining tenants by sales per square foot (highest first) and show how ` +
    `"${tenantName}" compares to its peers, including the category average and ` +
    `whether "${tenantName}" is above or below it. For each tenant include: ` +
    `space number, tenant name, GLA (sq ft), sales per sq ft, and occupancy cost %. ` +
    `Return the result as a table.`
  );
}
